import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, Plus, RefreshCw, GripVertical, Building2, MapPin,
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { id: 'Prospect', label: 'Prospect', color: 'bg-slate-100 border-slate-300' },
  { id: 'Intro Meeting Done', label: 'Intro Done', color: 'bg-blue-100 border-blue-300' },
  { id: 'Commercial Discussion', label: 'Commercial', color: 'bg-amber-100 border-amber-300' },
  { id: 'Terms Locked', label: 'Terms Locked', color: 'bg-purple-100 border-purple-300' },
  { id: 'Agreement Signed', label: 'Agreement', color: 'bg-emerald-100 border-emerald-300' },
  { id: 'Live', label: 'Live', color: 'bg-green-100 border-green-300' },
];

const BDPipeline = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  
  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    setLoading(true);
    try {
      const res = await api.getBDPipeline();
      setPipeline(res.data.pipeline || {});
    } catch (error) {
      console.error('Failed to load pipeline:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to BD Desk');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, project, stage) => {
    setDraggedItem({ project, fromStage: stage });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.fromStage === toStage) {
      setDraggedItem(null);
      return;
    }

    const { project, fromStage } = draggedItem;
    
    // Optimistic update
    const newPipeline = { ...pipeline };
    newPipeline[fromStage] = newPipeline[fromStage].filter(p => p.bd_project_id !== project.bd_project_id);
    newPipeline[toStage] = [...(newPipeline[toStage] || []), { ...project, stage: toStage }];
    setPipeline(newPipeline);

    try {
      await api.updateBDProjectStage(project.bd_project_id, toStage);
      toast.success(`Moved to ${toStage}`);
    } catch (error) {
      console.error('Failed to update stage:', error);
      toast.error('Failed to update stage');
      fetchPipeline(); // Revert
    }

    setDraggedItem(null);
  };

  // CSV Import Functions
  const downloadTemplate = async () => {
    try {
      const res = await api.getBDProjectImportTemplate();
      const template = res.data;
      
      // Create CSV content
      const headers = template.columns.map(c => c.name).join(',');
      const sampleRows = template.sample_rows.map(row => 
        template.columns.map(c => {
          const val = row[c.name] || '';
          return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
        }).join(',')
      ).join('\n');
      
      const csvContent = `${headers}\n${sampleRows}`;
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bd_projects_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const rows = parseCSV(text);
        
        if (rows.length === 0) {
          toast.error('No data found in CSV');
          return;
        }
        
        setImportData(rows);
        setImportResult(null);
        setShowImportModal(true);
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    e.target.value = '';
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      
      // Only add rows with required fields
      if (row.builder_name && row.project_name) {
        // Convert brokerage_percent to number if present
        if (row.brokerage_percent) {
          row.brokerage_percent = parseFloat(row.brokerage_percent) || null;
        }
        rows.push(row);
      }
    }
    
    return rows;
  };

  const handleImport = async () => {
    if (!importData || importData.length === 0) return;
    
    setImporting(true);
    try {
      const res = await api.importBDProjectsCSV({ projects: importData });
      setImportResult(res.data);
      
      if (res.data.created > 0 || res.data.updated > 0) {
        toast.success(`Import completed: ${res.data.created} created, ${res.data.updated} updated`);
        fetchPipeline();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResult({ error: error.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-full mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/bd')}
              >
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Onboarding Pipeline</h1>
                <p className="text-sm text-slate-500">Drag projects between stages</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv"
                className="hidden"
              />
              <Button variant="outline" onClick={downloadTemplate}>
                <Download size={16} className="mr-1" />
                Template
              </Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload size={16} className="mr-1" />
                Import CSV
              </Button>
              <Button onClick={() => navigate('/bd/projects/new')}>
                <Plus size={16} className="mr-1" />
                Add Project
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Board */}
      <div className="p-4 overflow-x-auto">
        <div className="flex gap-4 min-w-max">
          {STAGES.map((stage) => {
            const projects = pipeline[stage.id] || [];
            
            return (
              <div
                key={stage.id}
                className={`w-72 min-h-[calc(100vh-150px)] rounded-xl border-2 ${stage.color} p-3`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-700">{stage.label}</h3>
                  <Badge variant="outline" className="text-xs">
                    {projects.length}
                  </Badge>
                </div>

                {/* Project Cards */}
                <div className="space-y-2">
                  {projects.map((project) => (
                    <Card
                      key={project.bd_project_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, project, stage.id)}
                      className="p-3 bg-white cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/bd/projects/${project.bd_project_id}`)}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical size={16} className="text-slate-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-sm truncate">
                            {project.project_name}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {project.builder_name}
                          </p>
                          {project.location && (
                            <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                              <MapPin size={10} />
                              {project.location}
                            </p>
                          )}
                          {project.total_onboarding_days > 0 && (
                            <p className="text-xs text-purple-600 mt-1">
                              {project.total_onboarding_days} days in pipeline
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}

                  {projects.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      Drop projects here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CSV Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={20} />
              Import BD Projects from CSV
            </DialogTitle>
            <DialogDescription>
              Review the data below before importing. Builder names must match existing builders.
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <>
              {/* Preview Data */}
              <div className="mt-4">
                <div className="text-sm font-medium text-slate-700 mb-2">
                  {importData?.length || 0} rows to import:
                </div>
                <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Builder</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Project</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Location</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Stage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {importData?.slice(0, 50).map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                          <td className="px-3 py-2">{row.builder_name}</td>
                          <td className="px-3 py-2 font-medium">{row.project_name}</td>
                          <td className="px-3 py-2">{row.location || '-'}</td>
                          <td className="px-3 py-2">{row.stage || 'Prospect'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importData?.length > 50 && (
                    <div className="px-3 py-2 bg-slate-50 text-sm text-slate-500">
                      ... and {importData.length - 50} more rows
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-amber-600">
                  Note: Builder names must exactly match existing builders in the system.
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowImportModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleImport} disabled={importing}>
                  {importing ? (
                    <>
                      <RefreshCw size={16} className="mr-1 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-1" />
                      Import {importData?.length || 0} Projects
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Import Results */}
              <div className="mt-4 space-y-4">
                {importResult.error ? (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 text-red-700 font-medium">
                      <XCircle size={18} />
                      Import Failed
                    </div>
                    <p className="text-sm text-red-600 mt-1">{importResult.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                        <div className="flex items-center justify-center gap-1 text-emerald-700">
                          <CheckCircle size={18} />
                          <span className="text-2xl font-bold">{importResult.created}</span>
                        </div>
                        <div className="text-sm text-emerald-600">Created</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                        <div className="flex items-center justify-center gap-1 text-blue-700">
                          <RefreshCw size={18} />
                          <span className="text-2xl font-bold">{importResult.updated}</span>
                        </div>
                        <div className="text-sm text-blue-600">Updated</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                        <div className="flex items-center justify-center gap-1 text-red-700">
                          <XCircle size={18} />
                          <span className="text-2xl font-bold">{importResult.total_failed}</span>
                        </div>
                        <div className="text-sm text-red-600">Failed</div>
                      </div>
                    </div>

                    {importResult.failed?.length > 0 && (
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                          <AlertCircle size={16} />
                          Failed Rows
                        </div>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {importResult.failed.map((f, idx) => (
                            <div key={idx} className="text-sm p-2 bg-red-50 rounded">
                              <span className="font-medium">Row {f.row}:</span> {f.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-end">
                  <Button onClick={() => {
                    setShowImportModal(false);
                    setImportData(null);
                    setImportResult(null);
                  }}>
                    Done
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BDPipeline;
